import React, { useEffect, useMemo, useRef, useState } from "react";
import { resizeImageFileToDataUrl } from "../utils/imageUpload";

function sectionHeaderStyle(baseColor) {
  return {
    padding: "8px 12px",
    background: baseColor,
    borderBottom: "1px solid rgba(148,163,184,0.22)",
    fontSize: 13,
    fontWeight: 800,
    color: "#0f172a",
  };
}

function autoGrowTextarea(event) {
  const element = event.target;
  if (!element.value || !element.value.trim()) {
    element.style.height = "34px";
    return;
  }
  element.style.height = "0px";
  element.style.height = `${Math.max(34, element.scrollHeight)}px`;
}

function autoGrowTextareaElement(element) {
  if (!element) return;
  if (!element.value || !element.value.trim()) {
    element.style.height = "34px";
    return;
  }
  element.style.height = "0px";
  element.style.height = `${Math.max(34, element.scrollHeight)}px`;
}

export default function DataEntryPopup(props) {
  const {
    showVeriPopup,
    clickedInfo,
    sicaklik,
    atm,
    spc,
    tds,
    sal,
    ph,
    setSicaklik,
    setAtm,
    setSpc,
    setTds,
    setSal,
    setPh,
    notMetni,
    setNotMetni,
    customMeasurements,
    setCustomMeasurements,
    pointPhotos,
    setPointPhotos,
    entryStatus,
    setEntryStatus,
    pointPhoto,
    setPointPhoto,
    measurementTemplate,
    addTemplateRow,
    renameTemplateRow,
    deleteTemplateRow,
    moveTemplateRow,
    resetTemplateToDefault,
    inputStyle,
    onCancel,
    onSave,
  } = props;

  const [editMode, setEditMode] = useState(false);
  const [isPhotoProcessing, setIsPhotoProcessing] = useState(false);
  const [renameRowId, setRenameRowId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const maxPhotoCount = 4;

  useEffect(() => {
    if (showVeriPopup) {
      setEditMode(false);
      setIsPhotoProcessing(false);
      setRenameRowId(null);
      setRenameDraft("");
    }
  }, [showVeriPopup, clickedInfo?.id]);

  const bindMap = useMemo(
    () => ({
      sicaklik: { value: sicaklik ?? "", set: setSicaklik, type: "number" },
      atm: { value: atm ?? "", set: setAtm, type: "number" },
      spc: { value: spc ?? "", set: setSpc, type: "number" },
      tds: { value: tds ?? "", set: setTds, type: "number" },
      sal: { value: sal ?? "", set: setSal, type: "number" },
      ph: { value: ph ?? "", set: setPh, type: "number" },
      notMetni: { value: notMetni ?? "", set: setNotMetni, type: "textarea" },
    }),
    [
      sicaklik,
      atm,
      spc,
      tds,
      sal,
      ph,
      notMetni,
      setSicaklik,
      setAtm,
      setSpc,
      setTds,
      setSal,
      setPh,
      setNotMetni,
    ]
  );

  const rows = useMemo(() => {
    const tpl = Array.isArray(measurementTemplate) ? measurementTemplate : [];
    const sorted = [...tpl].sort((a, b) => (a?.order ?? 999) - (b?.order ?? 999));
    if (!editMode) return sorted.filter((r) => r?.visible !== false);
    return sorted;
  }, [measurementTemplate, editMode]);

  const safeInputStyle = inputStyle || {
    flex: 1,
    width: "100%",
    boxSizing: "border-box",
    padding: "6px 8px",
    border: "1px solid rgba(148,163,184,0.55)",
    borderRadius: "8px",
    fontSize: 14,
    background: "#ffffff",
  };

  if (!showVeriPopup) return null;

  const normalizedPointPhotos = (
    Array.isArray(pointPhotos)
      ? pointPhotos
      : typeof pointPhoto === "string" && pointPhoto.trim()
        ? [pointPhoto.trim()]
        : []
  )
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter(Boolean)
    .slice(0, maxPhotoCount);

  const setPhotos = (updater) => {
    if (typeof setPointPhotos === "function") {
      setPointPhotos(updater);
      return;
    }

    if (typeof setPointPhoto === "function") {
      if (typeof updater === "function") {
        const next = updater(normalizedPointPhotos);
        const nextFirst =
          Array.isArray(next) && typeof next[0] === "string" ? next[0] : "";
        setPointPhoto(nextFirst);
        return;
      }
      const nextFirst =
        Array.isArray(updater) && typeof updater[0] === "string" ? updater[0] : "";
      setPointPhoto(nextFirst);
    }
  };

  const canAddPhoto = normalizedPointPhotos.length < maxPhotoCount;

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(7, 17, 34, 0.58)",
    backdropFilter: "blur(3px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 10,
  };

  const panelStyle = {
    width: "min(720px, 98vw)",
    background: "#f8fafc",
    borderRadius: 16,
    border: "1px solid rgba(148,163,184,0.28)",
    boxShadow: "0 28px 70px rgba(2, 6, 23, 0.35)",
    overflow: "hidden",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
  };

  const headerStyle = {
    padding: "8px 12px",
    borderBottom: "1px solid rgba(148,163,184,0.28)",
    background: "#1f4aa8",
    color: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  };

  const titleStyle = {
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: "0.01em",
    margin: 0,
  };

  const subStyle = {
    fontSize: 12,
    marginTop: 4,
    opacity: 1,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 8px",
    borderRadius: 999,
    border: "1px solid rgba(191,219,254,0.65)",
    background: "rgba(15,23,42,0.22)",
  };

  const bodyStyle = {
    padding: 6,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  };

  const sectionCardStyle = {
    background: "#ffffff",
    border: "1px solid rgba(148,163,184,0.26)",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 6px 18px rgba(15,23,42,0.06)",
  };

  const sectionBodyStyle = {
    padding: "5px 7px",
  };

  const rowBaseStyle = {
    display: "grid",
    gridTemplateColumns: editMode ? "220px minmax(0, 1fr) 250px" : "220px minmax(0, 1fr)",
    gap: 4,
    alignItems: "center",
    padding: "2px 8px",
    borderBottom: "1px solid rgba(148,163,184,0.18)",
  };

  const labelStyle = {
    fontSize: 13,
    fontWeight: 700,
    color: "#1e293b",
  };

  const actionsStyle = {
    display: "flex",
    gap: 6,
    justifyContent: "flex-end",
    alignItems: "center",
    flexWrap: "wrap",
  };

  const btnStyle = {
    padding: "5px 8px",
    borderRadius: 7,
    border: "1px solid rgba(148,163,184,0.35)",
    background: "#f8fafc",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 700,
  };

  const primaryBtnStyle = {
    ...btnStyle,
    background: "#0f172a",
    color: "#fff",
    border: "1px solid #0f172a",
  };

  const dangerBtnStyle = {
    ...btnStyle,
    background: "#fff5f5",
    border: "1px solid rgba(220,38,38,.35)",
    color: "#b91c1c",
  };

  const mutedBtnStyle = {
    ...btnStyle,
    background: "#ffffff",
  };

  const closeBtnStyle = {
    width: 30,
    height: 30,
    borderRadius: 999,
    border: "1px solid rgba(248,113,113,0.75)",
    background: "linear-gradient(135deg, #ef4444, #dc2626)",
    color: "#fff",
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  };

  const saveBtnStyle = isPhotoProcessing
    ? { ...primaryBtnStyle, opacity: 0.65, cursor: "wait" }
    : primaryBtnStyle;

  const photoCameraBtnStyle = {
    ...btnStyle,
    padding: "4px 7px",
    fontSize: 11,
    background: "linear-gradient(135deg, #0ea5e9, #0284c7)",
    border: "1px solid #0284c7",
    color: "#fff",
  };

  const photoGalleryBtnStyle = {
    ...btnStyle,
    padding: "4px 7px",
    fontSize: 11,
    background: "linear-gradient(135deg, #22c55e, #16a34a)",
    border: "1px solid #16a34a",
    color: "#fff",
  };

  const sampleSelectStyle = {
    ...safeInputStyle,
    padding: "7px 10px",
    fontSize: 13,
    fontWeight: 700,
    border: "1px solid rgba(59,130,246,0.45)",
    background: "rgba(239,246,255,0.95)",
    color: "#0f172a",
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.65)",
  };

  const renameInputStyle = {
    ...safeInputStyle,
    width: 120,
    minWidth: 100,
    padding: "4px 6px",
    fontSize: 11,
    borderRadius: 7,
  };

  const handlePhotoFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!canAddPhoto) {
      alert("En fazla 4 fotoğraf ekleyebilirsiniz.");
      return;
    }

    setIsPhotoProcessing(true);
    try {
      const nextPhoto = await resizeImageFileToDataUrl(file);
      setPhotos((previous) => {
        const normalizedPrevious = (Array.isArray(previous) ? previous : [])
          .map((item) => (typeof item === "string" ? item.trim() : ""))
          .filter(Boolean)
          .slice(0, maxPhotoCount);
        if (normalizedPrevious.length >= maxPhotoCount) return normalizedPrevious;
        return [...normalizedPrevious, nextPhoto];
      });
    } catch (error) {
      alert(error?.message || "Fotoğraf işlenemedi.");
    } finally {
      setIsPhotoProcessing(false);
    }
  };

  const clearPhotos = () => {
    setPhotos([]);
  };

  const removePhotoAt = (indexToRemove) => {
    setPhotos((previous) => {
      const normalizedPrevious = (Array.isArray(previous) ? previous : [])
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean)
        .slice(0, maxPhotoCount);
      return normalizedPrevious.filter((_, index) => index !== indexToRemove);
    });
  };

  const renderValueInput = (row) => {
    const bind = row?.bind ?? null;

    if (bind && bindMap[bind]) {
      const meta = bindMap[bind];
      const val = (meta.value ?? "").toString();

      if (meta.type === "textarea" || row?.type === "textarea") {
        return (
          <textarea
            value={val}
            onChange={(e) => {
              meta.set(e.target.value);
              autoGrowTextarea(e);
            }}
            onInput={autoGrowTextarea}
            ref={autoGrowTextareaElement}
            rows={1}
            style={{
              ...safeInputStyle,
              minHeight: 34,
              height: 34,
              resize: "none",
              overflow: "hidden",
              lineHeight: 1.3,
            }}
            placeholder={row?.label || ""}
          />
        );
      }

      return (
        <input
          value={val}
          onChange={(e) => meta.set(e.target.value)}
          style={safeInputStyle}
          placeholder={row?.label || ""}
        />
      );
    }

    const id = row?.id;
    const currentVal =
      id && customMeasurements && typeof customMeasurements === "object"
        ? customMeasurements[id] ?? ""
        : "";

    const setVal = (v) => {
      if (!id) return;
      const next = { ...(customMeasurements || {}) };
      next[id] = v;
      setCustomMeasurements(next);
    };

    if (row?.type === "textarea") {
      return (
        <textarea
          value={(currentVal ?? "").toString()}
          onChange={(e) => {
            setVal(e.target.value);
            autoGrowTextarea(e);
          }}
          onInput={autoGrowTextarea}
          ref={autoGrowTextareaElement}
          rows={1}
          style={{
            ...safeInputStyle,
            minHeight: 34,
            height: 34,
            resize: "none",
            overflow: "hidden",
            lineHeight: 1.3,
          }}
          placeholder={row?.label || ""}
        />
      );
    }

    return (
      <input
        value={(currentVal ?? "").toString()}
        onChange={(e) => setVal(e.target.value)}
        style={safeInputStyle}
        placeholder={row?.label || ""}
      />
    );
  };

  const startRenameRow = (row) => {
    if (!row?.id) return;
    setRenameRowId(row.id);
    setRenameDraft(String(row?.label || ""));
  };

  const cancelRenameRow = () => {
    setRenameRowId(null);
    setRenameDraft("");
  };

  const submitRenameRow = (rowId) => {
    const nextLabel = String(renameDraft || "").trim();
    if (!rowId) return;
    if (!nextLabel) {
      alert("Alan adı boş olamaz.");
      return;
    }
    renameTemplateRow && renameTemplateRow(rowId, nextLabel);
    cancelRenameRow();
  };

  const handleSaveClick = async () => {
    let isSaved = true;
    if (typeof onSave === "function") {
      const saveResult = await onSave();
      isSaved = saveResult !== false;
    }
    if (isSaved && typeof onCancel === "function") {
      onCancel();
    }
  };

  const sampleTypeValue = String(customMeasurements?.numuneTuru ?? "");
  const waterSampleTypeValue = String(customMeasurements?.suNumuneTuru ?? "");
  const normalizedEntryStatus = String(entryStatus || "pending");
  const measurementEnabled = normalizedEntryStatus === "Pozitif";
  const resultStatusOptions = [
    { value: "Pozitif", label: "Pozitif" },
    { value: "Negatif", label: "Negatif" },
    { value: "Tekrar", label: "Tekrar" },
    { value: "pending", label: "Beklemede" },
  ];

  const coordBox = (() => {
    const y = clickedInfo?.lat;
    const x = clickedInfo?.lng;

    if (y == null || x == null) return null;

    return (
      <div style={sectionCardStyle}>
        <div
          style={{
            ...sectionBodyStyle,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 6,
            whiteSpace: "nowrap",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid rgba(14,116,144,0.26)",
              background: "rgba(224,242,254,0.9)",
            }}
          >
            <b>Koordinat Bilgisi:</b>
            <span>
              Y: {Number(y).toFixed(6)}, X: {Number(x).toFixed(6)}
            </span>
          </div>
        </div>
      </div>
    );
  })();

  return (
    <div style={overlayStyle}>
      <div style={panelStyle}>
        <div style={headerStyle}>
          <div>
            <div style={titleStyle}>Veri Girişi</div>
            <div style={subStyle}>
              Nokta:{" "}
              <b>
                {clickedInfo?.name ||
                  clickedInfo?.locationName ||
                  clickedInfo?.id ||
                  "-"}
              </b>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {measurementEnabled ? (
              <button
                style={mutedBtnStyle}
                onClick={() => setEditMode((v) => !v)}
                type="button"
              >
                {editMode ? "Şablon Modu: KAPAT" : "Şablon Düzenle"}
              </button>
            ) : null}

            {measurementEnabled && editMode ? (
              <>
                <button style={primaryBtnStyle} onClick={addTemplateRow} type="button">
                  + Satır Ekle
                </button>

                <button
                  style={dangerBtnStyle}
                  onClick={resetTemplateToDefault}
                  type="button"
                >
                  Varsayılanı Yükle
                </button>
              </>
            ) : null}

            <button style={closeBtnStyle} onClick={onCancel} type="button" aria-label="Kapat">
              X
            </button>
          </div>
        </div>

        <div style={bodyStyle}>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoFileChange}
            style={{ display: "none" }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoFileChange}
            style={{ display: "none" }}
          />

          {coordBox}

          <div style={sectionCardStyle}>
            <div style={sectionHeaderStyle("rgba(226, 232, 240, 0.9)")}>
              Durum
            </div>
            <div style={sectionBodyStyle}>
              <select
                value={normalizedEntryStatus}
                onChange={(event) =>
                  setEntryStatus && setEntryStatus(event.target.value)
                }
                style={sampleSelectStyle}
              >
                {resultStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={sectionCardStyle}>
            <div
              style={{
                ...sectionHeaderStyle("rgba(252, 211, 77, 0.48)"),
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <span>Ölçüm Formu</span>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                {isPhotoProcessing ? (
                  <span style={{ fontSize: 11, opacity: 0.75 }}>Hazırlanıyor...</span>
                ) : null}
                {normalizedPointPhotos.map((photoUrl, photoIndex) => (
                  <div
                    key={`photo_link_${photoIndex}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "2px 6px",
                      border: "1px solid rgba(148,163,184,0.45)",
                      borderRadius: 999,
                      background: "#f8fafc",
                      minHeight: 24,
                    }}
                  >
                    <a
                      href={photoUrl}
                      target="_blank"
                      rel="noreferrer"
                      title={`Fotoğraf ${photoIndex + 1}`}
                      style={{
                        color: "#0f4fd1",
                        textDecoration: "none",
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      Link {photoIndex + 1}
                    </a>
                    <button
                      style={{
                        border: "1px solid rgba(239,68,68,0.45)",
                        background: "#fff1f2",
                        color: "#b91c1c",
                        borderRadius: 999,
                        width: 20,
                        height: 20,
                        lineHeight: 1,
                        padding: 0,
                        cursor: "pointer",
                        fontWeight: 800,
                        fontSize: 12,
                      }}
                      type="button"
                      onClick={() => removePhotoAt(photoIndex)}
                      title="Kaldır"
                    >
                      X
                    </button>
                  </div>
                ))}
                <button
                  style={photoCameraBtnStyle}
                  type="button"
                  disabled={!canAddPhoto || isPhotoProcessing}
                  onClick={() => cameraInputRef.current?.click()}
                >
                  Fotoğraf Çek
                </button>
                <button
                  style={photoGalleryBtnStyle}
                  type="button"
                  disabled={!canAddPhoto || isPhotoProcessing}
                  onClick={() => galleryInputRef.current?.click()}
                >
                  Galeriden Seç
                </button>
                {normalizedPointPhotos.length > 0 ? (
                  <button style={dangerBtnStyle} type="button" onClick={clearPhotos}>
                    Tümünü Kaldır
                  </button>
                ) : null}
              </div>
            </div>
            {measurementEnabled ? (
            <div style={{ ...sectionBodyStyle, padding: 0 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: sampleTypeValue === "Su" ? "1fr 1fr" : "1fr",
                  gap: 6,
                  padding: "6px 8px",
                  borderBottom: "1px solid rgba(148,163,184,0.18)",
                  background: "rgba(219,234,254,0.35)",
                  borderRadius: 8,
                }}
              >
                <select
                  value={sampleTypeValue}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setCustomMeasurements((previous) => {
                      const next = { ...(previous || {}), numuneTuru: nextType };
                      if (nextType !== "Su") {
                        delete next.suNumuneTuru;
                      }
                      return next;
                    });
                  }}
                  style={sampleSelectStyle}
                >
                  <option value="">Numune türü seçiniz</option>
                  <option value="Su">Su</option>
                  <option value="Toprak">Toprak</option>
                  <option value="Dere Sedimanı">Dere Sedimanı</option>
                  <option value="Bitki">Bitki</option>
                  <option value="Pasa">Pasa</option>
                  <option value="Kayaç">Kayaç</option>
                  <option value="Diğer">Diğer</option>
                </select>

                {sampleTypeValue === "Su" ? (
                  <select
                    value={waterSampleTypeValue}
                    onChange={(e) =>
                      setCustomMeasurements((previous) => ({
                        ...(previous || {}),
                        suNumuneTuru: e.target.value,
                      }))
                    }
                    style={sampleSelectStyle}
                  >
                    <option value="">Seçiniz</option>
                    <option value="Kaynak">Kaynak</option>
                    <option value="Kuyu">Kuyu</option>
                    <option value="Akarsu">Akarsu</option>
                    <option value="Göl">Göl</option>
                  </select>
                ) : null}
              </div>

              {rows.length === 0 ? (
                <div style={{ padding: 10, opacity: 0.75, fontSize: 12 }}>Şablonda satır yok.</div>
              ) : (
                rows.map((row, idx) => {
                  const zebraStyle =
                    idx % 2 === 0
                      ? { background: "rgba(219, 234, 254, 0.75)" }
                      : { background: "rgba(254, 249, 195, 0.72)" };

                  return (
                    <div
                      key={row?.id || `${idx}_${row?.label || "row"}`}
                      style={{ ...rowBaseStyle, ...zebraStyle }}
                    >
                      <div style={labelStyle}>{row?.label || "Satır"}</div>
                      <div style={{ width: "100%" }}>{renderValueInput(row)}</div>

                      {editMode ? (
                        <div style={actionsStyle}>
                          <button
                            style={btnStyle}
                            type="button"
                            onClick={() =>
                              moveTemplateRow && moveTemplateRow(row?.id, "up")
                            }
                            disabled={idx === 0}
                          >
                            Yukarı
                          </button>

                          <button
                            style={btnStyle}
                            type="button"
                            onClick={() =>
                              moveTemplateRow && moveTemplateRow(row?.id, "down")
                            }
                            disabled={idx === rows.length - 1}
                          >
                            Aşağı
                          </button>

                          {renameRowId === row?.id ? (
                            <>
                              <input
                                type="text"
                                value={renameDraft}
                                onChange={(e) => setRenameDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    submitRenameRow(row?.id);
                                  }
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    cancelRenameRow();
                                  }
                                }}
                                style={renameInputStyle}
                                placeholder="Alan adı"
                                autoFocus
                              />
                              <button
                                style={primaryBtnStyle}
                                type="button"
                                onClick={() => submitRenameRow(row?.id)}
                              >
                                Kaydet
                              </button>
                              <button
                                style={btnStyle}
                                type="button"
                                onClick={cancelRenameRow}
                              >
                                Vazgeç
                              </button>
                            </>
                          ) : (
                            <button
                              style={btnStyle}
                              type="button"
                              onClick={() => startRenameRow(row)}
                            >
                              İsim
                            </button>
                          )}

                          <button
                            style={dangerBtnStyle}
                            type="button"
                            onClick={() =>
                              deleteTemplateRow && deleteTemplateRow(row?.id)
                            }
                          >
                            Kalıcı Sil
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })
              )}

            </div>
            ) : null}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 6,
              paddingTop: 0,
            }}
          >
            <button style={btnStyle} onClick={onCancel} type="button">
              İptal
            </button>
            <button
              style={saveBtnStyle}
              onClick={handleSaveClick}
              type="button"
              disabled={isPhotoProcessing}
            >
              Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

